import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge, Button, Card, Col, Form, Row, Spinner, Table, Alert,
} from 'react-bootstrap';
import QtyDual, { pickPurchasePackage, packageOptionLabel } from '../../utils/inventoryUnit';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../../layouts/PageLayout';
import { inventoryAPI } from '../../services';
import { purchaseOrderAPI, supplierMaterialAPI } from '../../services/purchaseOrder';
import { useBranch } from '../../context/BranchContext';
import { useAuth } from '../../context/AuthProvider';
import { toast } from 'react-toastify';

const fmtQty = (n) => Number(n ?? 0).toLocaleString('vi-VN', { maximumFractionDigits: 3 });
const fmtPrice = (n) => n != null ? Number(n).toLocaleString('vi-VN') + 'đ' : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('vi-VN') : '';

export default function CreatePurchaseOrderPage() {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();
  const { user } = useAuth();

  const [suppliers, setSuppliers] = useState([]);
  const [allMaterials, setAllMaterials] = useState([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [supplierItems, setSupplierItems] = useState([]); // NVL của NCC được chọn
  const [loadingSupplier, setLoadingSupplier] = useState(false);
  const [checkedIds, setCheckedIds] = useState(new Set()); // material_id đang checked

  const [orderItems, setOrderItems] = useState([]); // dòng trong bảng đặt hàng
  const [prePaid, setPrePaid] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [note, setNote] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [saving, setSaving] = useState(false);

  // Fetch suppliers + all materials
  useEffect(() => {
    Promise.all([
      inventoryAPI.getSuppliers(),
      inventoryAPI.getMaterials({ include_packages: true }),
    ]).then(([sup, mat]) => {
      setSuppliers(Array.isArray(sup) ? sup : sup?.data || []);
      setAllMaterials(Array.isArray(mat) ? mat : mat?.data || []);
    }).catch(() => toast.error('Không thể tải dữ liệu'));
  }, []);

  // Fetch NVL của NCC khi chọn NCC
  const fetchSupplierItems = useCallback(async (supplierId) => {
    if (!supplierId || !selectedBranch?.id) return;
    try {
      setLoadingSupplier(true);
      setCheckedIds(new Set());
      const data = await supplierMaterialAPI.listBySupplier(supplierId, selectedBranch.id);
      const items = Array.isArray(data) ? data : data?.data || [];
      setSupplierItems(items);
      // Auto-check NVL thiếu stock
      const autoChecked = new Set(items.filter((i) => i.is_low_stock).map((i) => i.material_id));
      setCheckedIds(autoChecked);
    } catch {
      toast.error('Không thể tải danh sách NVL của NCC');
    } finally {
      setLoadingSupplier(false);
    }
  }, [selectedBranch?.id]);

  const handleSupplierChange = (id) => {
    setSelectedSupplierId(id);
    setSupplierItems([]);
    if (id) fetchSupplierItems(id);
  };

  // Thêm NVL đã check vào bảng đặt hàng
  const addCheckedToOrder = () => {
    const toAdd = supplierItems.filter((si) => checkedIds.has(si.material_id));
    if (!toAdd.length) { toast.warn('Chọn ít nhất 1 NVL để thêm'); return; }

    setOrderItems((prev) => {
      const existingIds = new Set(prev.map((i) => i.material_id));
      const newItems = toAdd
        .filter((si) => !existingIds.has(si.material_id))
        .map((si) => {
          const availPkgs = allMaterials.find((m) => m.id === si.material_id)?.packages ?? [];
          // Quy cách NCC cấu hình có thể đã bị xoá → pickPurchasePackage bỏ qua và chọn lại
          const pkg = pickPurchasePackage(availPkgs, si.package);
          return {
            _key: si.material_id,
            supplier_id: selectedSupplierId,
            supplier_name: suppliers.find((s) => s.id === selectedSupplierId)?.name ?? '',
            material_id: si.material_id,
            material_name: si.material.name,
            material_unit: si.material.unit,
            package_id: pkg?.id ?? '',
            package_name: pkg?.unit_label ?? pkg?.name ?? '',
            package_qty_in_base: Number(pkg?.quantity_in_base ?? 1),
            available_packages: availPkgs,
            package_quantity: si.suggested_packages > 0 ? si.suggested_packages : 1,
            unit_price: si.default_price ? Number(si.default_price) : 0,
          };
        });

      // Update existing items that match material_id (update qty)
      const updated = prev.map((item) => {
        const si = toAdd.find((s) => s.material_id === item.material_id);
        if (si) {
          const qty = si.suggested_packages > 0 ? si.suggested_packages : 1;
          return { ...item, package_quantity: qty };
        }
        return item;
      });

      return [...updated, ...newItems];
    });

    toast.success(`Đã thêm ${toAdd.length} NVL vào đơn`);
    setCheckedIds(new Set());
  };

  // Thêm NVL tự do từ dropdown
  const [customMaterialId, setCustomMaterialId] = useState('');
  const addCustomMaterial = () => {
    if (!customMaterialId) return;
    const mat = allMaterials.find((m) => m.id === customMaterialId);
    if (!mat) return;
    if (orderItems.some((i) => i.material_id === customMaterialId)) {
      toast.warn('NVL này đã có trong đơn'); return;
    }
    const defaultPkg = pickPurchasePackage(mat.packages);
    setOrderItems((prev) => [...prev, {
      _key: customMaterialId,
      supplier_id: '',
      supplier_name: '',
      material_id: customMaterialId,
      material_name: mat.name,
      material_unit: mat.unit,
      package_id: defaultPkg?.id ?? '',
      package_name: defaultPkg?.unit_label ?? defaultPkg?.name ?? '',
      package_qty_in_base: Number(defaultPkg?.quantity_in_base ?? 1),
      available_packages: mat.packages ?? [],
      package_quantity: 1,
      unit_price: 0,
    }]);
    setCustomMaterialId('');
  };

  const updateOrderItem = (materialId, field, value) => {
    setOrderItems((prev) => prev.map((item) => {
      if (item.material_id !== materialId) return item;
      if (field === 'package_id') {
        const pkg = item.available_packages.find((p) => p.id === value);
        return {
          ...item,
          package_id: value,
          package_name: pkg?.unit_label ?? pkg?.name ?? '',
          package_qty_in_base: Number(pkg?.quantity_in_base ?? 1),
        };
      }
      return { ...item, [field]: value };
    }));
  };

  const removeOrderItem = (materialId) => {
    setOrderItems((prev) => prev.filter((i) => i.material_id !== materialId));
  };

  const subtotal = useMemo(
    () => orderItems.reduce((sum, i) => sum + (Number(i.package_quantity) * Number(i.unit_price)), 0),
    [orderItems],
  );
  const discountAmt = Math.min(Number(discount) || 0, subtotal);
  const netTotal = subtotal - discountAmt;

  const handleSave = async (asDraft) => {
    if (!selectedBranch?.id) { toast.error('Vui lòng chọn chi nhánh'); return; }
    if (!orderItems.length) { toast.error('Đơn phải có ít nhất 1 NVL'); return; }

    const items = orderItems.map((i) => ({
      supplier_id: i.supplier_id || undefined,
      material_id: i.material_id,
      package_id: i.package_id || undefined,
      package_quantity: Number(i.package_quantity),
      unit_price: Number(i.unit_price),
    }));

    try {
      setSaving(true);
      const po = await purchaseOrderAPI.create({
        branch_id: selectedBranch.id,
        items,
        pre_paid: prePaid,
        discount_amount: discountAmt > 0 ? discountAmt : undefined,
        note: note || undefined,
        expected_delivery_date: expectedDate || undefined,
      });

      if (!asDraft) {
        await purchaseOrderAPI.confirm(po.id);
        toast.success(`Đã xác nhận đơn ${po.order_number}`);
      } else {
        toast.success(`Đã lưu nháp đơn ${po.order_number}`);
      }
      navigate('/inventory/purchase-orders');
    } catch (err) {
      toast.error(err?.message || 'Không thể tạo đơn đặt hàng');
    } finally {
      setSaving(false);
    }
  };

  const toggleCheck = (materialId) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(materialId)) next.delete(materialId);
      else next.add(materialId);
      return next;
    });
  };

  const toggleAllCheck = () => {
    if (checkedIds.size === supplierItems.length) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(supplierItems.map((i) => i.material_id)));
    }
  };

  return (
    <PageLayout>
      <div className="container-fluid py-3">
        {/* Header */}
        <div className="d-flex align-items-center gap-3 mb-4">
          <Button variant="outline-secondary" size="sm" onClick={() => navigate(-1)}>← Quay lại</Button>
          <h4 className="mb-0 fw-bold">Tạo đơn đặt hàng NCC</h4>
          {selectedBranch && (
            <Badge bg="info" className="ms-auto">{selectedBranch.name}</Badge>
          )}
        </div>

        <Row className="g-3">
          {/* Left: Chọn NCC & thêm NVL */}
          <Col lg={5}>
            <Card className="shadow-sm">
              <Card.Header className="fw-semibold bg-white">
                1. Chọn NCC để thêm NVL
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Nhà cung cấp</Form.Label>
                  <Form.Select
                    value={selectedSupplierId}
                    onChange={(e) => handleSupplierChange(e.target.value)}
                  >
                    <option value="">-- Chọn NCC --</option>
                    {suppliers.filter((s) => s.is_active !== false).map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>

                {loadingSupplier && <div className="text-center py-3"><Spinner size="sm" /></div>}

                {supplierItems.length > 0 && (
                  <>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <small className="text-muted">{checkedIds.size}/{supplierItems.length} đã chọn</small>
                      <Form.Check
                        label="Chọn tất cả"
                        checked={checkedIds.size === supplierItems.length}
                        onChange={toggleAllCheck}
                      />
                    </div>
                    <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                      <Table size="sm" bordered hover className="mb-0">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th style={{ width: 36 }}></th>
                            <th>NVL</th>
                            <th className="text-end">Tồn</th>
                            <th className="text-end" style={{ whiteSpace: 'nowrap' }}>Đề xuất</th>
                          </tr>
                        </thead>
                        <tbody>
                          {supplierItems.map((si) => (
                            <tr
                              key={si.material_id}
                              className={si.is_low_stock ? 'table-danger' : ''}
                              style={{ cursor: 'pointer' }}
                              onClick={() => toggleCheck(si.material_id)}
                            >
                              <td>
                                <Form.Check
                                  checked={checkedIds.has(si.material_id)}
                                  onChange={() => toggleCheck(si.material_id)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </td>
                              <td>
                                <div className="fw-medium">{si.material.name}</div>
                                <small className="text-muted">
                                  {si.package?.unit_label ?? si.package?.name ?? si.material.unit}
                                  {si.default_price ? ` · ${fmtPrice(si.default_price)}` : ''}
                                </small>
                              </td>
                              <td className="text-end">
                                <small>
                                  <QtyDual quantityInBase={si.current_stock} material={si.material} packageOverride={si.package} />
                                </small>
                              </td>
                              <td className="text-end">
                                {si.suggested_packages > 0 ? (
                                  <Badge bg="warning" text="dark">{si.suggested_packages}</Badge>
                                ) : <span className="text-muted">—</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    <div className="mt-2">
                      <Button
                        variant="success"
                        size="sm"
                        className="w-100"
                        onClick={addCheckedToOrder}
                        disabled={!checkedIds.size}
                      >
                        ✓ Thêm {checkedIds.size} NVL đã chọn vào đơn
                      </Button>
                    </div>
                  </>
                )}

                {/* Thêm NVL tự do */}
                <hr />
                <Form.Label className="small fw-semibold">Thêm NVL khác (không theo NCC)</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Select
                    size="sm"
                    value={customMaterialId}
                    onChange={(e) => setCustomMaterialId(e.target.value)}
                  >
                    <option value="">-- Tìm NVL --</option>
                    {allMaterials
                      .filter((m) => m.is_active !== false && !orderItems.some((i) => i.material_id === m.id))
                      .map((m) => (
                        <option key={m.id} value={m.id}>{m.name} ({m.unit})</option>
                      ))}
                  </Form.Select>
                  <Button variant="outline-primary" size="sm" onClick={addCustomMaterial} disabled={!customMaterialId}>
                    + Thêm
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Right: Bảng đặt hàng + thông tin */}
          <Col lg={7}>
            <Card className="shadow-sm mb-3">
              <Card.Header className="fw-semibold bg-white d-flex justify-content-between align-items-center">
                <span>2. Danh sách đặt hàng ({orderItems.length} NVL)</span>
                {subtotal > 0 && (
                  <div className="text-end small lh-sm">
                    {discountAmt > 0 && (
                      <div className="text-muted">
                        <span>Tổng hàng: {fmtPrice(subtotal)}</span>
                        <span className="ms-2 text-danger">− {fmtPrice(discountAmt)}</span>
                      </div>
                    )}
                    <div className="fw-bold text-success fs-6">Thanh toán: {fmtPrice(netTotal)}</div>
                  </div>
                )}
              </Card.Header>
              <Card.Body className="p-0">
                {orderItems.length === 0 ? (
                  <p className="text-muted text-center py-4">
                    Chọn NCC và thêm NVL từ bảng bên trái
                  </p>
                ) : (
                  <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                    <Table size="sm" bordered hover responsive className="mb-0">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th>NVL</th>
                          <th style={{ width: 120 }}>Quy cách</th>
                          <th style={{ width: 80 }}>SL</th>
                          <th style={{ width: 120 }}>Giá/quy cách</th>
                          <th style={{ width: 110 }} className="text-end">Thành tiền</th>
                          <th style={{ width: 36 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderItems.map((item) => {
                          const lineAmt = Number(item.package_quantity) * Number(item.unit_price);
                          return (
                            <tr key={item.material_id}>
                              <td>
                                <div className="fw-medium">{item.material_name}</div>
                                {item.supplier_name && (
                                  <small className="text-muted">{item.supplier_name}</small>
                                )}
                              </td>
                              <td>
                                <Form.Select
                                  size="sm"
                                  value={item.package_id}
                                  onChange={(e) => updateOrderItem(item.material_id, 'package_id', e.target.value)}
                                >
                                  {item.available_packages.map((p) => (
                                    <option key={p.id} value={p.id}>
                                      {packageOptionLabel(p, item.material_unit)}
                                    </option>
                                  ))}
                                  {!item.available_packages.length && (
                                    <option value="">{item.material_unit}</option>
                                  )}
                                </Form.Select>
                              </td>
                              <td>
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  min="0.001"
                                  step="0.001"
                                  value={item.package_quantity}
                                  onChange={(e) => updateOrderItem(item.material_id, 'package_quantity', e.target.value)}
                                />
                              </td>
                              <td>
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  min="0"
                                  value={item.unit_price}
                                  onChange={(e) => updateOrderItem(item.material_id, 'unit_price', e.target.value)}
                                />
                              </td>
                              <td className="text-end fw-medium">
                                {lineAmt > 0 ? fmtPrice(lineAmt) : '—'}
                              </td>
                              <td className="text-center">
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="text-danger p-0"
                                  onClick={() => removeOrderItem(item.material_id)}
                                >✕</Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Thông tin thêm */}
            <Card className="shadow-sm">
              <Card.Header className="fw-semibold bg-white">3. Thông tin thêm</Card.Header>
              <Card.Body>
                <Row className="g-2">
                  <Col md={4}>
                    <Form.Label className="small">Ngày giao dự kiến</Form.Label>
                    <Form.Control
                      type="date"
                      size="sm"
                      value={expectedDate}
                      onChange={(e) => setExpectedDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                    />
                    <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                      Hiển thị cho NV khi kiểm hàng
                    </Form.Text>
                  </Col>
                  <Col md={4}>
                    <Form.Label className="small">Giảm giá / Chiết khấu</Form.Label>
                    <Form.Control
                      type="number"
                      size="sm"
                      min="0"
                      step="1000"
                      value={discount || ''}
                      onChange={(e) => setDiscount(e.target.value)}
                      placeholder="0"
                    />
                    <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                      Trừ vào tổng tiền thanh toán
                    </Form.Text>
                  </Col>
                  <Col md={4}>
                    <Form.Label className="small">Ghi chú</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      size="sm"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Ghi chú cho đơn đặt hàng..."
                    />
                  </Col>
                </Row>
                <Form.Check
                  className="mt-3"
                  type="checkbox"
                  id="pre-paid"
                  label="Thanh toán trước (tự động tạo phiếu chi CK đã duyệt)"
                  checked={prePaid}
                  onChange={(e) => setPrePaid(e.target.checked)}
                />
                {prePaid && netTotal > 0 && (
                  <Alert variant="info" className="mt-2 py-2 small">
                    Sẽ tạo phiếu chi{' '}
                    <strong>{fmtPrice(netTotal)}</strong>
                    {discountAmt > 0 && (
                      <span className="text-muted"> (sau giảm {fmtPrice(discountAmt)})</span>
                    )}{' '}
                    với trạng thái Đã thanh toán
                  </Alert>
                )}
              </Card.Body>
            </Card>

            {/* Actions */}
            <div className="d-flex gap-2 justify-content-end mt-3">
              <Button variant="outline-secondary" onClick={() => navigate(-1)} disabled={saving}>
                Hủy
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => handleSave(true)}
                disabled={saving || !orderItems.length}
              >
                {saving ? <Spinner size="sm" /> : 'Lưu nháp'}
              </Button>
              <Button
                variant="success"
                onClick={() => handleSave(false)}
                disabled={saving || !orderItems.length}
              >
                {saving ? <Spinner size="sm" /> : '✓ Xác nhận đặt hàng'}
              </Button>
            </div>
          </Col>
        </Row>
      </div>
    </PageLayout>
  );
}
